import { NETWORK_DISCONNECT_EVENT } from "@onmo/auth";

type Listener = () => void;

const OFFLINE_DEBOUNCE_MS = 2_500;

let listener: Listener | null = null;
let pending = false;

const handleDispatch = (): void => {
  if (pending) return;
  pending = true;
  listener?.();
};

if (typeof window !== "undefined") {
  let offlineTimer: ReturnType<typeof setTimeout> | null = null;

  const handleOffline = (): void => {
    if (offlineTimer) clearTimeout(offlineTimer);
    offlineTimer = setTimeout(() => {
      offlineTimer = null;
      if (navigator.onLine === false) handleDispatch();
    }, OFFLINE_DEBOUNCE_MS);
  };

  const handleOnline = (): void => {
    if (offlineTimer) {
      clearTimeout(offlineTimer);
      offlineTimer = null;
    }
    pending = false;
  };

  // While the app is backgrounded the redirect is deferred (we never show the
  // error page on a hidden app). When it returns to the foreground, re-notify
  // the listener if a disconnect is still pending so the redirect decision is
  // re-evaluated now that the app is visible.
  const handleVisibility = (): void => {
    if (document.visibilityState === "visible" && pending) listener?.();
  };

  // Source 1: lib-dispatched custom event (fetch-fail path)
  window.addEventListener(NETWORK_DISCONNECT_EVENT, handleDispatch);
  // Source 2: browser-native offline event (idle-detection path)
  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);
  // Source 3: app returning to the foreground (suspend/resume path)
  document.addEventListener("visibilitychange", handleVisibility);
}

export const onErrorNetwork = (fn: Listener): (() => void) => {
  listener = fn;
  if (pending) fn();
  return () => {
    if (listener === fn) listener = null;
  };
};

// Called by the ErrorNetwork page when the user clicks Try Again and network is connected
export const resetErrorNetwork = (): void => {
  pending = false;
};
